// ============================================================================
// Gmail — `users.watch` / `users.stop` helpers for Pub/Sub push delivery.
//
// Watch/Stop flow:
//   1. Operator creates a Google Cloud Pub/Sub topic and grants publish
//      access to `gmail-api-push@system.gserviceaccount.com` (Google docs
//      spell out the IAM binding required — see inline note below).
//   2. Operator configures a Pub/Sub push subscription pointing at our
//      ingress route (`/api/v1/connectors/webhooks/gmail/:instanceId`).
//   3. We call `users.watch` with `{ topicName, labelIds, labelFilterBehavior }`
//      — Google now fans changes for that mailbox out to the topic.
//   4. `users.watch` responses expire after ~7 days; callers must refresh
//      before expiry (scheduler job).
//   5. `users.stop` terminates the watch.
//
// The webhook itself (`webhook.ts`) decodes the Pub/Sub envelope + runs
// `history.list` to resolve the actual message ids.
// ============================================================================

import { GMAIL_API_BASE } from './oauth'
import {
  classifyGmailError,
  GmailError,
  parseRetryAfterMs,
  sanitizeGmailErrorMessage,
  type GmailWatchResponse,
  type GoogleErrorEnvelope,
} from './types'

// ============================================================================
// Types
// ============================================================================

export interface SubscribeGmailInboxInput {
  accessToken: string
  /**
   * Full Pub/Sub topic name: `projects/{projectId}/topics/{topic}`. IMPORTANT:
   * the Pub/Sub topic MUST have granted `pubsub.publisher` to
   * `gmail-api-push@system.gserviceaccount.com` — Google's Gmail API push
   * service publishes on behalf of EVERY mailbox. Without the grant Gmail
   * returns `FAILED_PRECONDITION`.
   */
  pubsubTopic: string
  /** Restrict to specific labels. Defaults to `['INBOX']`. */
  labelIds?: string[]
  /** `include` (default) or `exclude`. */
  labelFilterBehavior?: 'include' | 'exclude'
  fetchImpl?: typeof fetch
  apiBase?: string
}

export interface SubscribeGmailInboxOutput {
  /** The baseline historyId — start of the history.list window. */
  historyId: string
  /** ISO 8601 expiry (derived from Google's millisecond value). */
  expiration: string
}

export interface UnsubscribeGmailInboxInput {
  accessToken: string
  fetchImpl?: typeof fetch
  apiBase?: string
}

// ============================================================================
// subscribeGmailInbox
// ============================================================================

export async function subscribeGmailInbox(
  args: SubscribeGmailInboxInput,
): Promise<SubscribeGmailInboxOutput> {
  assertAccessToken(args.accessToken)
  if (!args.pubsubTopic || !args.pubsubTopic.startsWith('projects/')) {
    throw new GmailError(
      'INVALID_INPUT',
      'pubsubTopic must be in the form `projects/{projectId}/topics/{topic}`',
    )
  }
  const fetchImpl = args.fetchImpl ?? fetch
  const apiBase = args.apiBase ?? GMAIL_API_BASE
  // Round 1 H3: an EXPLICIT empty `labelIds: []` is rewritten to INBOX
  // rather than "watch everything" — watching every label inflates Pub/Sub
  // traffic (drafts, sent, spam, trash, all label changes) and is almost
  // never the caller's intent. Callers that genuinely want every label
  // should pass `['INBOX', 'SENT', 'SPAM', ...]` explicitly.
  const labelIds = Array.isArray(args.labelIds) && args.labelIds.length > 0
    ? args.labelIds
    : ['INBOX']
  const labelFilterBehavior = args.labelFilterBehavior ?? 'include'

  const url = `${apiBase}/gmail/v1/users/me/watch`
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${args.accessToken}`,
    },
    body: JSON.stringify({
      topicName: args.pubsubTopic,
      labelIds,
      labelFilterBehavior,
    }),
  })

  if (!resp.ok) {
    throw await errorFromResponse(resp)
  }

  const body = (await resp.json()) as GmailWatchResponse
  if (!body || typeof body.historyId !== 'string' || typeof body.expiration !== 'string') {
    throw new GmailError(
      'PROVIDER_ERROR',
      'Gmail returned an unexpected users.watch shape',
    )
  }
  const expirationMs = Number(body.expiration)
  const expiration = Number.isFinite(expirationMs)
    ? new Date(expirationMs).toISOString()
    : body.expiration
  return { historyId: body.historyId, expiration }
}

// ============================================================================
// unsubscribeGmailInbox
// ============================================================================

export async function unsubscribeGmailInbox(
  args: UnsubscribeGmailInboxInput,
): Promise<void> {
  assertAccessToken(args.accessToken)
  const fetchImpl = args.fetchImpl ?? fetch
  const apiBase = args.apiBase ?? GMAIL_API_BASE

  const url = `${apiBase}/gmail/v1/users/me/stop`
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${args.accessToken}`,
    },
  })
  // `users.stop` returns 204 No Content on success. Any non-2xx is surfaced.
  if (!resp.ok) {
    throw await errorFromResponse(resp)
  }
}

// ============================================================================
// Helpers
// ============================================================================

async function errorFromResponse(response: Response): Promise<GmailError> {
  let envelope: GoogleErrorEnvelope | null = null
  let text = ''
  try {
    text = await response.text()
    envelope = text ? (JSON.parse(text) as GoogleErrorEnvelope) : null
  } catch {
    envelope = null
  }
  const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'))
  if (!envelope && text) {
    return new GmailError(
      response.status === 401 ? 'AUTH_FAILED' : 'PROVIDER_ERROR',
      sanitizeGmailErrorMessage(text).slice(0, 200),
      { httpStatus: response.status, retryAfterMs },
    )
  }
  return classifyGmailError(
    response.status,
    envelope?.error?.errors?.[0]?.reason,
    envelope,
    retryAfterMs,
  )
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new GmailError('INVALID_INPUT', 'accessToken is required')
  }
}
